<script setup>
import { onMounted, ref } from "vue"
import get from 'lodash.get'
import AppStaffItem from "@/views/AppStaffItem.vue"
import { NOTIFICATION_FLAG } from "@/helpers/ConstantHelper"
import useProject from "@/stores/project.js"

const props = defineProps({
  badgeProps: {
    type: null,
    required: false,
    default: undefined,
  },
  location: {
    type: null,
    required: false,
    default: 'bottom end',
  },
})


const socketStatus = useEvent()
const { data: sessionData } = useAuth()
const user = get(sessionData, 'value.user')
const LIMIT = 5
const notifications = ref([])
let intervalId = null

// const { $echo } = useNuxtApp() // sử dụng khi dùng web sockket

const deleteAllNotifications = async () => {
  await useApiRequest(`notifications/delete_all`, { method: "POST", local: false })
  await getData()
}

const readAll = async () => {
  await useApiRequest(`notifications/mark-all-read`, { method: "POST", local: false })
  await getData()
}

const gotoList = async () => {
  navigateTo('/notifications', { external: true })
}

const total = ref("0")
const totalUnread = ref("0")

const getData = async () => {
  // eslint-disable-next-line sonarjs/prefer-object-literal
  const options = {}

  options.params = { limit: 20 }

  const { data } = await useApiRequest('notifications', options)

  notifications.value = get(data, 'value.data.data')
  total.value = get(data, 'value.data.total')
  totalUnread.value = get(data, 'value.data.total_unread')
  if (totalUnread.value > 20) {
    totalUnread.value = "20+"
  }
  if (total.value > 20) {
    total.value = "20+"
  }
}

async function removeNotification(notification) {
  await useApiRequest(`notifications/${notification.id}`, {
    method: "DELETE",
  })
  await getData()
}

async function handleClickNotification(notification, noRedirect = false) {
  await useApiRequest(`notifications/update/` + notification.id, {
    method: "put", body: {
      read_flag: !notification.read_flag ? NOTIFICATION_FLAG.READ : NOTIFICATION_FLAG.UNREAD,
    },
  })

  if (!noRedirect && get(notification, 'click_url')) {
    navigateTo(get(notification, 'click_url'), { external: true, open: true })
  }
  await getData()
}

const handleNewNotification = ev => {
  getData()
  const type = ev?.data?.type
  switch (type) {
  case "project": {
    const projectCode = ev?.data?.project_code
    const route = useRoute()
    if (route.name === "projects-code" && projectCode) {
      const store = useProject(projectCode)

      store.fetch()
    }

    console.log(route, projectId)
    break
  }
  }
  if (ev) {
    notifications.value = ev?.data?.data
    total.value = ev?.data.total
    if (total.value > 20) {
      total.value = "20+"
    }
  }
}

onMounted(() => {
  nextTick(() => getData())
  intervalId = setInterval(getData, 900000) // gọi API mỗi 15 phút

  if (user?.id) {
    socketStatus.addEventListener('notifications_' + user.id, ".newNotify", handleNewNotification, true)
  }
})

onUnmounted(() => {
  clearInterval(intervalId) // tránh leak
  if (user?.id) {
    socketStatus.removeEventListener('notifications_' + user.id, ".newNotify", handleNewNotification, true)
  }
})
</script>

<template>
  <IconBtn
    id="notification-btn"
    class="me-4"
  >
    <VBadge
      v-bind="badgeProps"
      color="error"
      :model-value="!!totalUnread"
      :content="totalUnread"
      class="notification-badge"
    >
      <VIcon
        size="26"
        icon="tabler-bell"
        class="pt-2 ps-2"
      />
    </VBadge>

    <VMenu
      activator="parent"
      width="460px"
      :location="location"
      offset="14px"
      :close-on-content-click="false"
    >
      <VCard class="d-flex flex-column">
        <!-- 👉 Header -->
        <VCardItem class="notification-section">
          <VCardTitle class="text-lg">
            Thông báo
          </VCardTitle>

          <template #append>
            <IconBtn
              v-show="get(notifications, 'length')"
              @click="readAll"
            >
              <VIcon icon="tabler-eye" />

              <VTooltip
                activator="parent"
                location="start"
              >
                Xem toàn bộ tin nhắn
              </VTooltip>
            </IconBtn>
            <IconBtn
              v-show="get(notifications, 'length')"
              @click="deleteAllNotifications"
            >
              <VIcon icon="tabler-trash" />

              <VTooltip
                activator="parent"
                location="start"
              >
                Xóa toàn bộ thông báo
              </VTooltip>
            </IconBtn>
          </template>
        </VCardItem>

        <VDivider />

        <!-- 👉 Notifications list -->
        <VList
          v-if="get(notifications, 'length')"
          class="notification-list rounded-0 py-0"
        >
          <template
            v-for="(notification, index) in notifications"
            :key="notification.id"
          >
            <VDivider v-if="index > 0" />
            <VListItem
              v-tooltip="`${notification.message}`"
              link
              lines="three"
              min-height="66px"
              class="list-item-hover-class"
            >
              <!-- Slot: Prepend -->
              <!-- Handles Avatar: Image, Icon, Text -->

              <div @click="handleClickNotification(notification)">
                <VListItemTitle class="font-weight-medium justify-space-between">
                  {{ notification?.title }}
                  <span
                    class="text-xs text-disabled"
                    style="float: right;"
                  >
                    {{ notification?.time }}
                  </span>
                </VListItemTitle>
                <VListItemSubtitle>{{ get(notification, 'message') }}</VListItemSubtitle>
                <VListItemAction class="justify-end">
                  <AppStaffItem
                    v-if="get(notification, 'sender')"
                    :staff="notification.sender"
                    :avatar-size="32"
                  />
                </VListItemAction>
              </div>

              <!-- Slot: Append -->
              <template #append>
                <div class="d-flex flex-column align-center gap-4">
                  <VBadge
                    dot
                    :color="!notification.read_flag ? 'primary' : '#a8aaae'"
                    :class="`${notification.read_flag ? 'visible-in-hover' : ''} ms-1`"
                    @click.prevent.stop="() => handleClickNotification(notification, true)"
                  />

                  <div style="block-size: 28px; inline-size: 28px;">
                    <IconBtn
                      size="small"
                      class="visible-in-hover"
                      @click="() => removeNotification(notification)"
                    >
                      <VIcon
                        size="20"
                        icon="tabler-x"
                      />
                    </IconBtn>
                  </div>
                </div>
              </template>
            </VListItem>
          </template>
        </VList>
        <VList v-else>
          <VListItem
            class="text-center text-medium-emphasis"
            style="block-size: 56px;"
          >
            <VListItemTitle>Không có thông báo nào được tìm thấy!</VListItemTitle>
          </VListItem>
        </VList>

        <VDivider />

        <!-- 👉 Footer -->
        <VCardActions
          v-show="get(notifications, 'length')"
          class="notification-footer"
        >
          <VBtn
            block
            @click="gotoList"
          >
            Xem tất cả thông báo
          </VBtn>
        </VCardActions>
      </VCard>
    </VMenu>
  </IconBtn>
</template>

<style lang="scss">
.notification-section {
  padding: 14px !important;
}

.notification-footer {
  padding: 6px !important;
}

.list-item-hover-class {
  .visible-in-hover {
    display: none;
  }

  &:hover {
    .visible-in-hover {
      display: block;
    }
  }
}

.notification-list.v-list {
  .v-list-item {
    border-radius: 0 !important;
    margin: 0 !important;

    &[tabindex="-2"]:not(.v-list-item--active) {
      &:hover,
      &:focus-visible {
        color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));

        .v-list-item-subtitle {
          color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
        }
      }
    }
  }
}

// Badge Style Override for Notification Badge
.notification-badge {
  .v-badge__badge {
    /* stylelint-disable-next-line liberty/use-logical-spec */
    min-width: 18px;
    block-size: 18px;
    padding: 4px;
  }
}
</style>
